import styled from "@emotion/styled";
import { Component } from "react";

import { DesignToken, SpacingToken, Text, Tooltip } from "@livechat/design-system-react-components";

import { Pagination } from "components/pagination";
import { TicketsRoutes } from "constants/tickets/TicketsRoutes";
import { MAX_SEARCH_RESULTS } from "constants/tickets/search";
import { formatCounterValue, isCounterValueRounded } from "helpers/numbers";
import { TICKETS_PER_PAGE } from "store/views/tickets/reducer";
import { addCommas, plural } from "utils/string";

interface IProps {
  selectedIds: string[];
  route: TicketsRoutes;
  ticketsFromOtherTeamsAllowed: boolean;
  currentPage: number;
  totalPages: number;
  totalResults: number;
  liveCount?: number;
  shouldIgnoreRealtimeCounter?: boolean;
  isDataFresh?: boolean;
  supportsBatchUpdate?: boolean;
  onPageChange(page: number): void;
  onAllTicketsLinkClick(): void;
}

export class TicketsPagination extends Component<IProps> {
  shouldComponentUpdate(nextProps: IProps): boolean {
    return nextProps.isDataFresh !== false || nextProps.selectedIds !== this.props.selectedIds;
  }

  render(): JSX.Element {
    const {
      currentPage,
      totalPages,
      supportsBatchUpdate,
      selectedIds,
      totalResults,
      liveCount,
      shouldIgnoreRealtimeCounter,
      onPageChange,
    } = this.props;

    const isLive = !shouldIgnoreRealtimeCounter && liveCount != null;
    const effectiveCount = isLive ? liveCount : totalResults;
    const effectiveTotalPages = isLive ? Math.ceil((liveCount + 1 + 0.01) / TICKETS_PER_PAGE) : totalPages;

    const selectedTicketsMessage = supportsBatchUpdate && selectedIds.length > 0 ? `${selectedIds.length} of ` : "";

    const countLabel = isLive
      ? formatCounterValue(effectiveCount)
      : effectiveCount <= MAX_SEARCH_RESULTS
        ? addCommas(effectiveCount)
        : `${addCommas(MAX_SEARCH_RESULTS)}+`;

    const ticketsText = (
      <TotalResults size="sm">
        {selectedTicketsMessage}
        {countLabel} {plural(effectiveCount, "ticket", "tickets")}
      </TotalResults>
    );

    return (
      <Wrapper>
        {isLive && isCounterValueRounded(effectiveCount) ? (
          <Tooltip placement="top" kind="invert" triggerRenderer={ticketsText}>
            {effectiveCount.toLocaleString("en-US")}
          </Tooltip>
        ) : (
          ticketsText
        )}
        <Pagination
          currentPage={currentPage}
          totalPages={effectiveTotalPages}
          totalPagesLimit={500}
          onPageChange={onPageChange}
        />
      </Wrapper>
    );
  }
}

const Wrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: var(${SpacingToken.Spacing8});
  border-bottom: 1px solid var(${DesignToken.BorderBasicSecondary});
  padding: 0 var(${SpacingToken.Spacing2});
`;

const TotalResults = styled(Text)`
  color: var(${DesignToken.ContentBasicSecondary});
`;
